<template>
    <div v-if="canListMasterTemplates">
        <hox-loading-layer v-if="isLoading" is-opaque />
        <MasterTemplateLibrary class="master-template-library-position" />

        <Sidebar :scroll="false">
            <div class="master-templates__filters">
                <MasterTemplateSizeFilter />
                <MasterTemplateTypeFilter />
                <MasterTemplateResizedFilter />
                <MasterTemplateFamilyFilter />
            </div>
        </Sidebar>
    </div>
</template>

<script lang="ts">
import { defineComponent } from "vue";
import { type NavigationGuardNext, type RouteLocationNormalized } from "vue-router";
import bus from "@/bus";
import { UiAction } from "@/store/modules/ui";

import clientMasterTemplatesQuery from "@/apollo/queries/v2/ClientMasterTemplates.gql";

import Sidebar from "@/components/Sidebar.vue";
import MasterTemplateLibrary from "@/components/Campaign/MasterTemplateLibrary.vue";
import MasterTemplateSizeFilter from "@/components/Campaign/MasterTemplates/MasterTemplateSizeFilter.vue";
import MasterTemplateTypeFilter from "@/components/Campaign/MasterTemplates/MasterTemplateTypeFilter.vue";
import MasterTemplateResizedFilter from "@/components/Campaign/MasterTemplates/MasterTemplateResizedFilter.vue";
import MasterTemplateFamilyFilter from "@/components/Campaign/MasterTemplates/MasterTemplateFamilyFilter.vue";
import type { MasterTemplate } from "@/types";
import { useGA4Tracking } from "@/composables/useGA4Tracking";
import { GA4_EVENT_TYPES, GA4_FEATURE_CATEGORIES, GA4_FEATURE_CONTEXTS } from "@/enums/ga4Events";

export default defineComponent({
    name: "CampaignTemplates",
    components: {
        MasterTemplateFamilyFilter,
        MasterTemplateResizedFilter,
        MasterTemplateTypeFilter,
        MasterTemplateSizeFilter,
        Sidebar,
        MasterTemplateLibrary
    },

    beforeRouteLeave(to: RouteLocationNormalized, from: RouteLocationNormalized, next: NavigationGuardNext) {
        if (to.name !== from.name) {
            this.ga4.trackFeatureEvent({
                featureEvent: GA4_EVENT_TYPES.FEATURE_COMPLETE,
                featureCategory: GA4_FEATURE_CATEGORIES.MASTER_TEMPLATES,
                featureContext: GA4_FEATURE_CONTEXTS.MASTER_TEMPLATES
            });
        }
        next();
    },

    setup() {
        const ga4 = useGA4Tracking();

        return {
            ga4
        };
    },

    data() {
        return {
            masterTemplates: [] as MasterTemplate[],
            isLoading: true
        };
    },

    computed: {
        canListMasterTemplates() {
            return this.$auth.userCan(this.$auth.Actions.CanListMasterTemplates, { clientId: this.clientId });
        },

        clientId() {
            return this.$route.params.clientId as string;
        }
    },

    watch: {
        canListMasterTemplates: {
            immediate: true,
            handler() {
                if (this.canListMasterTemplates) {
                    return;
                }

                this.$snackbar.warning("You do not have access to list master templates");
                this.$router.push({ name: "Home" });
            }
        }
    },

    created() {
        this.ga4.trackFeatureEvent({
            featureEvent: GA4_EVENT_TYPES.FEATURE_ACTIVATE,
            featureCategory: GA4_FEATURE_CATEGORIES.MASTER_TEMPLATES,
            featureContext: GA4_FEATURE_CONTEXTS.MASTER_TEMPLATES
        });

        if (!this.canListMasterTemplates) {
            return;
        }

        this.$store.dispatch(UiAction.OpenSidebar);
        this.$apollo.queries.masterTemplates.refetch();
    },

    apollo: {
        masterTemplates: {
            query: clientMasterTemplatesQuery,
            variables() {
                return {
                    clientId: this.clientId
                };
            },
            manual: true,
            skip() {
                return !this.canListMasterTemplates;
            },
            result({ data }: { data: { masterTemplatesByClient: MasterTemplate[] } }) {
                this.masterTemplates = data.masterTemplatesByClient;

                this.isLoading = false;
            },
            error(e) {
                bus.$emit("apolloErrorEvent", e as Error);
            }
        }
    }
});
</script>

<style lang="scss">
@use "@style/variables" as *;

.master-template-library-position {
    position: absolute;
    top: 0;
    right: 0;
    bottom: 0;
    left: 0;
}

.master-templates__filters {
    padding-top: 60px;
}
</style>
